import { useCallback, useState } from "react";
import Flex from "@/components/Flex";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import StyledFormModal from "@/components/globalModal/StyledModal";
import { CHECKBOX_MODAL_SX, SESSION_NOTE_LABEL } from "../group-visit-constant";
import { SessionCheckboxSection } from "../group-visit-types";
import CheckboxOptionSections from "./CheckboxOptionSections";

export type CheckboxOptionsModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  sections: SessionCheckboxSection[];
  /** Current value of every field the sections own, keyed by field name. */
  values: Record<string, string>;
  onSave: (values: Record<string, string>) => void;
  disabled?: boolean;
};

/**
 * Option picker for a checkbox-card section. Edits stay in a draft until Save,
 * so the note behind the modal is untouched while it is being filled in.
 *
 * @author Rajkumar Rathod
 */
const CheckboxOptionsModal = ({
  open,
  onClose,
  title,
  sections,
  values,
  onSave,
  disabled,
}: CheckboxOptionsModalProps) => {
  const [draft, setDraft] = useState<Record<string, string>>(values);

  const handleChange = useCallback(
    (field: string, value: string) =>
      setDraft((prev) => ({ ...prev, [field]: value })),
    []
  );

  const handleSave = () => {
    onSave(draft);
    onClose();
  };

  return (
    <StyledFormModal
      open={open}
      onClose={onClose}
      dialogTitle={title}
      dialogAction={
        <>
          <StyledButtonV2
            label={SESSION_NOTE_LABEL.cancel}
            variant="outlined"
            onClick={onClose}
          />
          <StyledButtonV2
            label={SESSION_NOTE_LABEL.save}
            variant="primary"
            onClick={handleSave}
            disabled={disabled}
          />
        </>
      }
      sx={CHECKBOX_MODAL_SX}
    >
      <Flex flexDirection="column" gap={2} width="100%" height="100%" pt={2}>
        <CheckboxOptionSections
          sections={sections}
          values={draft}
          onChange={handleChange}
          disabled={disabled}
        />
      </Flex>
    </StyledFormModal>
  );
};

export default CheckboxOptionsModal;
